import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tabs,
  Typography,
  message,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { CompanyStructureCurrent } from './structure-current'
import { CompanyStructureV1Tree } from './structure-v1-tree'
import { CompanyStructureV2Split } from './structure-v2-split'
import { CompanyStructureV3Panels } from './structure-v3-panels'
import { CompanyStructureV4Table } from './structure-v4-table'
import type { CompanyStructure, Office, Organization, RoleItem } from './types'

const { Paragraph, Title } = Typography

const VARIANT_META: Array<{
  key: string
  label: string
  idea: string
  functional: string
}> = [
  {
    key: 'current',
    label: 'Текущий',
    idea: 'Вложенные Collapse + карточки сотрудников. Эталон с /home.',
    functional: 'Всё развёрнуто по умолчанию; статус тегом + opacity; invite на офисе.',
  },
  {
    key: 'v1',
    label: 'Дерево',
    idea: 'Иерархия отступами и линиями. Без карточек и без тега «Неактивен».',
    functional: 'Переключатель «только активные» скрывает ветки. Pending/expired остаются видимы.',
  },
  {
    key: 'v2',
    label: 'Split',
    idea: 'Слева орг., справа офисы-вкладки и список сотрудников.',
    functional: 'Один офис в фокусе. Invite только если орг. и офис активны.',
  },
  {
    key: 'v3',
    label: 'Панели',
    idea: 'Офисы как отдельные панели-колонки; статус полоской слева.',
    functional: 'Поиск по сотрудникам фильтрует панели. Неактивные приглушены без тега.',
  },
  {
    key: 'v4',
    label: 'Таблица',
    idea: 'Админский вид: org → office → Table.',
    functional: 'Неактивные офисы свёрнуты. Статус одной колонкой, пагинация при >8.',
  },
]

export default function CompanyUiLabPage() {
  const navigate = useNavigate()
  const [variant, setVariant] = useState('current')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [structure, setStructure] = useState<CompanyStructure | null>(null)
  const [roles, setRoles] = useState<RoleItem[]>([])
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteTarget, setInviteTarget] = useState<{ org: Organization; office: Office } | null>(
    null,
  )
  const [form] = Form.useForm()

  const breadcrumbs = useMemo(
    () => [{ href: '/home', title: 'Главная' }, { title: 'UI: Компания' }],
    [],
  )

  const meta = VARIANT_META.find(v => v.key === variant) || VARIANT_META[0]

  const loadStructure = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchAPI('/api/home/company-structure')
      if (res?.errors) {
        setError(res.errors?.[0]?.detail || 'Не удалось загрузить структуру')
        return
      }
      setStructure(res?.data || null)
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStructure()
    ;(async () => {
      try {
        const res = await fetchAPI('/api/roles/list')
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
        setRoles(
          list
            .filter((r: any) => r?.is_active !== false)
            .map((r: any) => ({ id: Number(r.id), name: String(r.name || '') })),
        )
      } catch {
        /* non-blocking */
      }
    })()
  }, [loadStructure])

  const openInvite = useCallback(
    (org: Organization, office: Office) => {
      setInviteTarget({ org, office })
      form.resetFields()
      setInviteOpen(true)
    },
    [form],
  )

  const submitInvite = async (values: any) => {
    if (!inviteTarget) return
    setInviteLoading(true)
    try {
      const res = await fetchAPI('/api/home/employees/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: String(values.email || '').trim(),
          organization_id: inviteTarget.org.id,
          office_id: inviteTarget.office.id,
          role_ids: values.role_ids || [],
        }),
      })
      if (res?.errors) {
        message.error(res.errors?.[0]?.detail || 'Не удалось отправить приглашение')
        return
      }
      message.success(res?.data?.message || 'Приглашение отправлено')
      setInviteOpen(false)
      await loadStructure()
    } catch (e: any) {
      message.error(e?.message || 'Ошибка отправки')
    } finally {
      setInviteLoading(false)
    }
  }

  const view =
    structure &&
    {
      current: <CompanyStructureCurrent structure={structure} onInvite={openInvite} />,
      v1: <CompanyStructureV1Tree structure={structure} onInvite={openInvite} />,
      v2: <CompanyStructureV2Split structure={structure} onInvite={openInvite} />,
      v3: <CompanyStructureV3Panels structure={structure} onInvite={openInvite} />,
      v4: <CompanyStructureV4Table structure={structure} onInvite={openInvite} />,
    }[variant]

  return (
    <B2BLayout pageTitle="UI lab: Компания" breadcrumbs={breadcrumbs}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Варианты блока «Компания»
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Песочница: UI и UX могут отличаться, функционал — любой, пока логичен. На /home пока
            эталон «Текущий».
          </Paragraph>
        </div>

        <Card>
          <Tabs
            activeKey={variant}
            onChange={setVariant}
            items={VARIANT_META.map(v => ({ key: v.key, label: v.label }))}
          />
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={meta.idea}
            description={`Логика: ${meta.functional}`}
          />

          {loading && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          )}
          {error && <Alert type="error" showIcon message={error} />}
          {!loading && !error && structure && view}
        </Card>
      </Space>

      <Modal
        title="Пригласить сотрудника"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={inviteLoading}
        okText="Отправить приглашение"
        destroyOnClose
      >
        {inviteTarget && (
          <Paragraph type="secondary">
            {inviteTarget.org.name} → {inviteTarget.office.name}
            {structure?.invite_ttl_hours
              ? `. Ссылка будет действительна ${structure.invite_ttl_hours} ч.`
              : ''}
          </Paragraph>
        )}
        <Form form={form} layout="vertical" onFinish={submitInvite}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Укажите email' },
              { type: 'email', message: 'Введите корректный email' },
            ]}
          >
            <Input placeholder="employee@example.ru" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="role_ids"
            label="Роли"
            rules={[
              {
                validator: (_, value) =>
                  value && value.length
                    ? Promise.resolve()
                    : Promise.reject(new Error('Выберите хотя бы одну роль')),
              },
            ]}
          >
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              options={roles.map(r => ({ label: r.name, value: r.id }))}
            />
          </Form.Item>
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ padding: 0 }}
            onClick={() => navigate('/admin/roles')}
          >
            Управление ролями
          </Button>
        </Form>
      </Modal>
    </B2BLayout>
  )
}
